/**
 * BeforeAfterView Component
 *
 * Shows original vs revised story content and analysis side by side.
 * Used in Revision Editor to compare improvements.
 */

import { motion } from 'framer-motion'
import { ArrowRight, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/Card'
import { StructureChecklist, ScoreDisplay } from '@/components/challenge/StructureChecklist'
import {
  type StructureAnalysisResult,
  type StoryElement,
  STORY_ELEMENT_INFO,
} from '@/types/domain'
import { calculateStoryScore, compareAnalyses } from '@/services/structureAnalyzer'

// =============================================================================
// Before/After Analysis View
// =============================================================================

interface BeforeAfterViewProps {
  originalAnalysis: StructureAnalysisResult
  revisedAnalysis: StructureAnalysisResult
  className?: string
}

export function BeforeAfterView({
  originalAnalysis,
  revisedAnalysis,
  className,
}: BeforeAfterViewProps) {
  const originalScore = calculateStoryScore(originalAnalysis)
  const revisedScore = calculateStoryScore(revisedAnalysis)
  const improvement = compareAnalyses(originalAnalysis, revisedAnalysis)

  return (
    <div className={cn('space-y-6', className)}>
      {/* Score Comparison */}
      <ScoreComparison
        originalScore={originalScore}
        revisedScore={revisedScore}
      />

      {/* Element Comparison */}
      <ElementComparison
        originalAnalysis={originalAnalysis}
        revisedAnalysis={revisedAnalysis}
        improvement={improvement}
      />
    </div>
  )
}

// =============================================================================
// Score Comparison
// =============================================================================

interface ScoreComparisonProps {
  originalScore: number
  revisedScore: number
}

function ScoreComparison({ originalScore, revisedScore }: ScoreComparisonProps) {
  const difference = revisedScore - originalScore
  const isImproved = difference > 0
  const isSame = difference === 0

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <Card variant="elevated">
        <CardContent className="p-6">
          <h3 className="font-display font-bold text-lg text-[var(--color-kids-text)] text-center mb-4">
            Your Progress
          </h3>

          <div className="flex items-center justify-center gap-6">
            {/* Original Score */}
            <div className="text-center">
              <p className="text-xs text-[var(--color-kids-text-muted)] mb-1">Before</p>
              <ScoreDisplay score={originalScore} className="scale-75" />
            </div>

            {/* Arrow */}
            <div className="flex flex-col items-center">
              <ArrowRight className="w-8 h-8 text-[var(--color-kids-primary)]" />
              {!isSame && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className={cn(
                    'mt-2 flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold',
                    isImproved
                      ? 'bg-[var(--color-kids-success)]/10 text-[var(--color-kids-success)]'
                      : 'bg-[var(--color-kids-warning)]/10 text-[var(--color-kids-warning)]'
                  )}
                >
                  {isImproved ? (
                    <>
                      <TrendingUp className="w-4 h-4" />
                      +{difference}
                    </>
                  ) : (
                    <>
                      <TrendingDown className="w-4 h-4" />
                      {difference}
                    </>
                  )}
                </motion.div>
              )}
            </div>

            {/* Revised Score */}
            <div className="text-center">
              <p className="text-xs text-[var(--color-kids-text-muted)] mb-1">After</p>
              <ScoreDisplay score={revisedScore} className="scale-75" />
            </div>
          </div>

          {/* Encouragement message */}
          <p className="text-center mt-4 text-sm text-[var(--color-kids-text-muted)]">
            {getEncouragementMessage(difference)}
          </p>
        </CardContent>
      </Card>
    </motion.div>
  )
}

function getEncouragementMessage(difference: number): string {
  if (difference >= 30) return 'Wow! You made huge improvements! Your story is much stronger now!'
  if (difference >= 15) return 'Great job! Your revisions really made a difference!'
  if (difference >= 5) return 'Nice work! Every improvement counts!'
  if (difference > 0) return 'Good start! Keep revising to make your story even better!'
  if (difference === 0) return 'Try adding or improving some story elements to boost your score!'
  return 'Don\'t worry! Revising takes practice. Try focusing on one element at a time.'
}

// =============================================================================
// Element Comparison
// =============================================================================

interface ElementComparisonProps {
  originalAnalysis: StructureAnalysisResult
  revisedAnalysis: StructureAnalysisResult
  improvement: ReturnType<typeof compareAnalyses>
}

function ElementComparison({
  originalAnalysis,
  revisedAnalysis,
  improvement,
}: ElementComparisonProps) {
  return (
    <div className="space-y-4">
      <h3 className="font-display font-bold text-lg text-[var(--color-kids-text)]">
        Element by Element
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Original */}
        <Card>
          <CardContent className="p-4">
            <h4 className="font-display font-semibold text-sm text-[var(--color-kids-text-muted)] mb-3">
              Original Story
            </h4>
            <StructureChecklist
              analysis={originalAnalysis}
              mode="coach"
              expandable={false}
            />
          </CardContent>
        </Card>

        {/* Revised */}
        <Card>
          <CardContent className="p-4">
            <h4 className="font-display font-semibold text-sm text-[var(--color-kids-text-muted)] mb-3">
              Revised Story
            </h4>
            <StructureChecklist
              analysis={revisedAnalysis}
              mode="summary"
              expandable={false}
              highlightElements={improvement.improved as StoryElement[]}
            />
          </CardContent>
        </Card>
      </div>

      {/* Improvement Summary */}
      {improvement.improved.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="bg-[var(--color-kids-success)]/5 border-[var(--color-kids-success)]/20">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <TrendingUp className="w-5 h-5 text-[var(--color-kids-success)] flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-display font-semibold text-sm text-[var(--color-kids-success)]">
                    Improvements Made
                  </h4>
                  <p className="text-sm text-[var(--color-kids-text-muted)] mt-1">
                    You improved:{' '}
                    {improvement.improved.map((el, i) => (
                      <span key={el}>
                        <span className="font-medium text-[var(--color-kids-text)]">
                          {STORY_ELEMENT_INFO[el as StoryElement].label}
                        </span>
                        {i < improvement.improved.length - 1 && ', '}
                      </span>
                    ))}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  )
}

// =============================================================================
// Side-by-Side Content View (for comparing text)
// =============================================================================

interface ContentComparisonProps {
  originalContent: string
  revisedContent: string
  className?: string
}

export function ContentComparison({
  originalContent,
  revisedContent,
  className,
}: ContentComparisonProps) {
  const hasChanges = originalContent !== revisedContent

  return (
    <div className={cn('grid grid-cols-1 md:grid-cols-2 gap-4', className)}>
      {/* Original */}
      <Card>
        <CardContent className="p-4">
          <h4 className="font-display font-semibold text-sm text-[var(--color-kids-text-muted)] mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[var(--color-kids-text-muted)]" />
            Original
          </h4>
          <div className="prose prose-sm max-w-none text-[var(--color-kids-text)]">
            <p className="whitespace-pre-wrap">{originalContent}</p>
          </div>
        </CardContent>
      </Card>

      {/* Revised */}
      <Card className={cn(hasChanges && 'ring-2 ring-[var(--color-kids-success)]/30')}>
        <CardContent className="p-4">
          <h4 className="font-display font-semibold text-sm text-[var(--color-kids-text-muted)] mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[var(--color-kids-success)]" />
            Revised
            {hasChanges && (
              <span className="text-xs bg-[var(--color-kids-success)]/10 text-[var(--color-kids-success)] px-2 py-0.5 rounded-full">
                Updated
              </span>
            )}
          </h4>
          <div className="prose prose-sm max-w-none text-[var(--color-kids-text)]">
            <p className="whitespace-pre-wrap">{revisedContent}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// =============================================================================
// Mini Comparison Badge (for list views)
// =============================================================================

interface ImprovementBadgeProps {
  improvement: number
  className?: string
}

export function ImprovementBadge({ improvement, className }: ImprovementBadgeProps) {
  if (improvement === 0) {
    return (
      <span className={cn(
        'inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full',
        'bg-[var(--color-kids-surface-alt)] text-[var(--color-kids-text-muted)]',
        className
      )}>
        <Minus className="w-3 h-3" />
        No change
      </span>
    )
  }

  const isPositive = improvement > 0

  return (
    <span className={cn(
      'inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium',
      isPositive
        ? 'bg-[var(--color-kids-success)]/10 text-[var(--color-kids-success)]'
        : 'bg-[var(--color-kids-warning)]/10 text-[var(--color-kids-warning)]',
      className
    )}>
      {isPositive ? (
        <>
          <TrendingUp className="w-3 h-3" />
          +{improvement}
        </>
      ) : (
        <>
          <TrendingDown className="w-3 h-3" />
          {improvement}
        </>
      )}
    </span>
  )
}

export default BeforeAfterView
